import React from "react";
import { ActivityLog } from "@/services/activityLogService";
import { dashboardNestedCardClassName } from "@/components/pages/dashboard/components/DashboardCard";
import { Badge } from "@/components/ui/badge";
import { User } from "lucide-react";
import { statusToBadgeVariant } from "@/components/pages/dashboard/components/activityLogStatus";
import { useNavigate } from "react-router-dom";
import {
  formatActivityActorName,
  formatActivityTitle,
  formatTime,
} from "@/utils/formatActivityLogDisplay";

interface ActivityLogItemProps {
  log: ActivityLog;
}

const toNormalized = (value: string | null): string =>
  value?.toLowerCase() ?? "";

const getRequestId = (log: ActivityLog): string | null => {
  const rawRequestId = log.activity.requestId;
  if (typeof rawRequestId !== "string") return null;
  const value = rawRequestId.trim();
  return value || null;
};

const getReservationId = (log: ActivityLog): string | null => {
  const rawReservationId = log.activity.reservationId ?? log.reservationId;
  if (typeof rawReservationId === "string") {
    const value = rawReservationId.trim();
    if (value) return value;
  }

  const rawBookingId = log.bookingId;
  if (typeof rawBookingId !== "string") return null;
  const value = rawBookingId.trim();
  return value || null;
};

const getNavigationPath = (log: ActivityLog): string | null => {
  const service = toNormalized(log.service);
  const action = toNormalized(log.activity.action);
  const type = toNormalized(
    typeof log.activity.type === "string" ? log.activity.type : null,
  );

  if (action === "checkout_feedback_created") {
    const reservationId = getReservationId(log);
    return reservationId
      ? `/reservations/${encodeURIComponent(reservationId)}?tab=other`
      : null;
  }

  const isRequestLog = service.includes("request");
  if (isRequestLog) {
    const requestId = getRequestId(log);
    return requestId ? `/requests/${encodeURIComponent(requestId)}` : null;
  }

  const isFeedbackOrCheckinCheckoutLog =
    service.includes("feedback") ||
    service.includes("checkin") ||
    service.includes("checkout") ||
    action.includes("feedback") ||
    action.includes("checkin") ||
    action.includes("check-in") ||
    action.includes("check_in") ||
    action.includes("checkout") ||
    action.includes("check-out") ||
    action.includes("check_out") ||
    type.includes("feedback") ||
    type.includes("checkin") ||
    type.includes("checkout");

  if (isFeedbackOrCheckinCheckoutLog) {
    const reservationId = getReservationId(log);
    return reservationId
      ? `/reservations/${encodeURIComponent(reservationId)}`
      : null;
  }

  return null;
};

export const ActivityLogItem: React.FC<ActivityLogItemProps> = ({ log }) => {
  const navigate = useNavigate();
  const actionLabel = formatActivityTitle(log);
  const actorLabel = formatActivityActorName(log);
  const navigationPath = getNavigationPath(log);
  const isInteractive = navigationPath !== null;

  const onOpenLinkedEntity = () => {
    if (!navigationPath) return;
    navigate(navigationPath);
  };

  const interactiveProps = isInteractive
    ? {
        role: "button" as const,
        tabIndex: 0,
        onClick: onOpenLinkedEntity,
        onKeyDown: (event: React.KeyboardEvent<HTMLDivElement>) => {
          if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            onOpenLinkedEntity();
          }
        },
      }
    : {};

  return (
    <div
      className={dashboardNestedCardClassName(
        `flex flex-col items-start gap-2.5 px-2.5 py-2 ${
          isInteractive
            ? "cursor-pointer transition-opacity hover:opacity-90"
            : ""
        }`,
      )}
      {...interactiveProps}
    >
      <div className="grid grid-cols-12">
        <Badge
          variant="service_gray_sm"
          className="max-h-4 w-fit shrink-0 bg-monochrome-core-2 text-white col-span-2"
        >
          {log.activity.roomNumber ?? ""}
        </Badge>
        <div className="flex min-w-0 items-center gap-1.5 col-span-6">
          <span className="font-sans text-xs font-medium text-eathy-comfort-1">
            {actionLabel}
          </span>
        </div>
        <time
          className="shrink-0 text-end font-sans text-xs font-light text-foreground col-span-4"
          dateTime={log.createdAt}
        >
          {formatTime(log.createdAt)}
        </time>
      </div>
      {log.activity.status && (
        <div className="grid grid-cols-2 gap-1 w-full">
          <Badge
            variant={statusToBadgeVariant(log.activity.status)}
            className="h-6 w-full shrink-0 col-span-1"
          >
            {log.activity.status}
          </Badge>
          <Badge
            variant="new_secondary"
            className="flex h-6 w-full shrink-0 items-center gap-1 overflow-hidden whitespace-nowrap text-ellipsis text-black col-span-1"
          >
            <User className="shrink-0" />
            <span className="min-w-0 truncate">{actorLabel}</span>
          </Badge>
        </div>
      )}
    </div>
  );
};
